import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Youtube, Instagram, Facebook, MessageCircle } from "lucide-react";
import { SITE } from "@/constants/site";

export const Footer = () => (
  <footer data-testid="site-footer" className="relative mt-24 rounded-t-[3rem] bg-brand-navy text-sky-50 grain overflow-hidden">
    <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
      <div className="grid gap-12 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src="/images/logo.png" alt="Blooming Chaitanya Academy logo" className="h-12 w-12 rounded-full object-contain bg-white ring-2 ring-brand-sun/60" />
            <div className="leading-tight">
              <p className="font-display font-semibold text-lg">Blooming Chaitanya Academy</p>
              <p className="text-xs uppercase tracking-[0.2em] text-sky-300">Est. 2012 · Murlipura, Jaipur</p>
            </div>
          </div>
          <p className="text-sky-200/80 text-sm leading-relaxed max-w-xs">
            A warm, play-based preschool, daycare and primary school in Murlipura, Jaipur — RBSE pattern, Toddler to Class 8 — where little minds bloom with confidence, values and joy.
          </p>
          <p data-testid="footer-sanskrit" className="mt-6 font-deva text-brand-sun text-xl">{SITE.sanskrit}</p>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-5 text-brand-sun">Visit Us</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="h-5 w-5 shrink-0 text-brand-sky" />
              <a href={SITE.mapLink} target="_blank" rel="noreferrer" data-testid="footer-address" className="hover:text-white transition-colors">
                {SITE.address}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone className="h-5 w-5 shrink-0 text-brand-sky" />
              <span>
                <a href={`tel:${SITE.phone1}`} data-testid="footer-phone-1" className="hover:text-white transition-colors">{SITE.phone1Display}</a>
                <span className="text-sky-400"> · </span>
                <a href={`tel:${SITE.phone2}`} data-testid="footer-phone-2" className="hover:text-white transition-colors">{SITE.phone2Display}</a>
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="h-5 w-5 shrink-0 text-brand-sky" />
              <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="hover:text-white transition-colors">{SITE.email}</a>
            </li>
            <li className="flex gap-3">
              <Clock className="h-5 w-5 shrink-0 text-brand-sky" />
              <span data-testid="footer-hours">{SITE.hours}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-5 text-brand-sun">Explore</h4>
          <ul className="space-y-3 text-sm mb-8">
            {[["/", "Home"], ["/about", "About Us"], ["/programs", "Programs"], ["/admissions", "Admissions"]].map(([to, label]) => (
              <li key={to}>
                <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`} className="text-sky-200/80 hover:text-white transition-colors">
                  {label}
                </Link>
              </li>
            ))}
            <li>
              <a href="/files/parent-handbook.pdf" download data-testid="footer-handbook-link" className="text-brand-sun hover:text-white transition-colors font-semibold">
                Parent Handbook (PDF)
              </a>
            </li>
          </ul>
          <div className="flex gap-3">
            {[
              [SITE.socials.youtube, Youtube, "youtube"],
              [SITE.socials.instagram, Instagram, "instagram"],
              [SITE.socials.facebook, Facebook, "facebook"],
              [SITE.socials.whatsapp, MessageCircle, "whatsapp"],
            ].map(([href, Icon, name]) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`footer-social-${name}`}
                aria-label={name}
                className="grid h-11 w-11 place-items-center rounded-full bg-white/10 text-sky-100 transition-all duration-300 hover:bg-brand-coral hover:scale-110"
              >
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-14 border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-sky-300/70">
        <p>© {new Date().getFullYear()} Blooming Chaitanya Academy, Jaipur. All rights reserved.</p>
        <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white transition-colors">Admin Login</Link>
      </div>
    </div>
  </footer>
);
